// EG-55 — preparo do dado dos dois gráficos da Visão geral por empresa.
//
// O backend passou a entregar pillars[].themes[] (contrato aditivo, AD-3). Este
// módulo traduz esse payload para o formato que o recharts consome, e é aqui
// que moram as decisões da story — por isso ele é puro e testado direto, sem
// passar por render.
//
// A regra que sustenta o gráfico 1: a pilha é EXAUSTIVA. A soma dos segmentos
// de um pilar é a nota dele, e é só por isso que o topo da barra pode ser
// comparado com a linha de corte (AD-1). Tema não tem corte — fragmentar é
// composição, nunca avaliação (AD-2).

// Uma matiz por pilar: o pilar continua legível de longe mesmo com a barra
// fatiada. Os temas variam só a luminosidade dentro da matiz do pilar.
const MATIZES = [205, 145, 32, 275, 350, 190];
const SATURACAO = 68;

// Faixa de luminosidade dos tons de um mesmo pilar. Larga o bastante para
// separar 5 temas a olho (o máximo medido por pilar dentro de um questionário)
// sem chegar no branco nem no preto.
const LUM_MIN = 36;
const LUM_MAX = 72;

const numero = (valor) => Number(valor ?? 0);

/**
 * A matiz de um pilar.
 *
 * Dentro da paleta base, as cores escolhidas — a tela já foi aprovada com elas e
 * trocá-las para resolver o 7º pilar seria pagar com o que já funciona.
 *
 * Além dela, passo do ângulo áureo (137.5°), que é o que espalha matizes sem
 * cair em cima das anteriores. O `% MATIZES.length` de antes fazia o 7º pilar
 * receber a MESMA cor do 1º — falha silenciosa num gráfico onde a cor é
 * justamente o que identifica o pilar. Máximo medido hoje: 3 pilares por
 * certificado; o catálogo tem 47.
 */
const matizDoPilar = (indice) => {
  if (indice < MATIZES.length) return MATIZES[indice];

  const passo = (indice - MATIZES.length + 1) * 137.5;
  return Math.round((MATIZES[MATIZES.length - 1] + passo) % 360);
};

export function corDoPilar(indice) {
  return `hsl(${matizDoPilar(indice)}, ${SATURACAO}%, 46%)`;
}

function tomDoTema(indicePilar, posicao, total) {
  const luminosidade =
    total <= 1
      ? 46
      : LUM_MIN + Math.round((posicao * (LUM_MAX - LUM_MIN)) / (total - 1));

  return `hsl(${matizDoPilar(indicePilar)}, ${SATURACAO}%, ${luminosidade}%)`;
}

/**
 * Os temas de um pilar, mas SÓ se eles ainda formarem uma pilha exaustiva.
 *
 * O backend já derruba a fragmentação quando a soma não fecha, e a medição diz
 * que pilar parcialmente classificado não existe (0 de 178 pares mistos). Mas o
 * contrato é aditivo e os deploys são independentes — este front pode conversar
 * com um backend anterior à guarda. Como é esta tela que desenha a comparação
 * com o corte, é ela que não pode desenhar uma pilha que mente sobre a distância
 * até a aprovação (AD-1/AD-5).
 */
function temasExaustivos(pilar) {
  const temas = pilar.themes ?? [];
  if (temas.length === 0) return [];

  // O contrato declara isso desde a EG-55 (themes_exhaustive). Quando o campo
  // vem, ele manda: o backend soma sobre o dado completo, a tela vê só o que o
  // payload trouxe.
  if (typeof pilar.themes_exhaustive === 'boolean') {
    return pilar.themes_exhaustive ? temas : [];
  }

  // Sem o campo, o cálculo local. Contrato aditivo com deploys independentes:
  // este front pode estar conversando com um backend anterior ao campo.
  const soma = temas.reduce((acc, t) => acc + numero(t.score), 0);
  return +soma.toFixed(2) === +numero(pilar.score).toFixed(2) ? temas : [];
}

/**
 * Os segmentos de um pilar: um por tema, ou um único com o nome do pilar.
 *
 * A chave da série sai do id_theme, nunca do título: o catálogo só garante
 * título único DENTRO do pilar, então dois pilares podem ter temas homônimos.
 * Chavear por título fundiria os dois numa faixa só e empilharia a nota de um
 * pilar dentro do outro.
 */
function segmentosDoPilar(pilar, indice) {
  const temas = temasExaustivos(pilar);
  const id_pillar = Number(pilar.id_pillar);

  if (temas.length === 0) {
    return [
      {
        key: `pilar-${id_pillar}`,
        nome: pilar.name_pillar,
        id_pillar,
        cor: corDoPilar(indice),
        valor: numero(pilar.score),
      },
    ];
  }

  // AD-6: a ordem chega pronta do backend (ORDER BY t.index, t.title).
  // Reordenar aqui desfaria o desempate por título e os segmentos voltariam a
  // trocar de lugar entre uma requisição e outra.
  return temas.map((t, posicao) => ({
    key: `tema-${t.id_theme}`,
    nome: t.title,
    id_pillar,
    cor: tomDoTema(indice, posicao, temas.length),
    valor: numero(t.score),
  }));
}

/**
 * Gráfico 1 — Nota por pilar × corte.
 *
 * Devolve as linhas (uma por pilar, como hoje) e as séries que viram as <Bar>
 * empilhadas. Cada linha carrega SÓ as chaves das próprias séries: a série de
 * outro pilar fica ausente, nunca zero. Zero apareceria na legenda e no tooltip
 * como se aquele pilar tivesse o tema valendo nada.
 *
 * `corte` continua uma coluna da linha, não uma propriedade da série — a linha
 * de corte é do pilar e não se fragmenta (AD-2).
 */
export function montarPilhaDeTemas(pillars) {
  const lista = pillars ?? [];
  const series = [];

  const rows = lista.map((pilar, indice) => {
    const row = {
      id_pillar: Number(pilar.id_pillar),
      pillar: pilar.name_pillar,
      nota: numero(pilar.score),
      corte: numero(pilar.cutoff_score),
      passed: Boolean(pilar.certified),
    };

    segmentosDoPilar(pilar, indice).forEach(({ valor, ...serie }) => {
      series.push(serie);
      row[serie.key] = valor;
    });

    return row;
  });

  return { rows, series };
}

/**
 * Gráfico 2 / B1 — radar de perfil.
 *
 * Fragmentado, os eixos são os temas e o polígono de CORTE sai: comparar um
 * eixo-tema com o corte do pilar seria comparar parte com total (AD-2).
 *
 * Sem tema nenhum — o caso de 100% dos certificados de produção hoje — os eixos
 * continuam sendo os pilares, e sobre pilar o corte significa exatamente o que
 * sempre significou. Tirá-lo também aqui removeria informação da tela de todo
 * mundo em troca de nada.
 */
export function montarPerfil(pillars) {
  const lista = pillars ?? [];
  const fragmentado = lista.some((pilar) => temasExaustivos(pilar).length > 0);

  const eixos = lista.flatMap((pilar, indice) => {
    const temas = temasExaustivos(pilar);
    const id_pillar = Number(pilar.id_pillar);

    if (temas.length > 0) {
      return temas.map((t, posicao) => ({
        eixo: t.title,
        nota: numero(t.score),
        id_pillar,
        cor: tomDoTema(indice, posicao, temas.length),
      }));
    }

    const eixo = {
      eixo: pilar.name_pillar,
      nota: numero(pilar.score),
      id_pillar,
      cor: corDoPilar(indice),
    };

    return [fragmentado ? eixo : { ...eixo, corte: numero(pilar.cutoff_score) }];
  });

  return { eixos, fragmentado };
}

// montarTreemap viveu aqui. Saiu quando o TL escolheu o radar (AD-7) — a AD-8
// manda apagar o perdedor na mesma entrega, e preparo de dado sem gráfico que o
// consuma é código morto.

/**
 * Gráfico de nota por TEMA — uma barra por tema, agrupadas por pilar.
 *
 * Diferente do gráfico 1, onde o tema é fatia dentro da barra do pilar: aqui
 * cada tema tem barra própria, para comparar temas entre si. Sem linha de
 * corte, pelo mesmo motivo de sempre — o corte é do pilar e incide sobre a soma
 * (AD-2).
 *
 * Pilar sem tema NÃO entra: um gráfico rotulado "por tema" exibindo pilar
 * mentiria sobre o que está mostrando. Quando nenhum pilar tem tema (hoje, 100%
 * dos certificados de produção), a lista sai vazia e quem explica é a tela.
 */
export function montarBarrasDeTema(pillars) {
  return (pillars ?? []).flatMap((pilar, indice) => {
    // Temas CRUS, não exaustivos — de propósito, e essa é a diferença em relação
    // à pilha do gráfico 1.
    //
    // Exaustividade é requisito de comparar o topo da pilha com a LINHA DE CORTE.
    // Este gráfico não tem corte: mostrar os temas que existem não mente sobre
    // nada. Exigir exaustividade aqui escondia classificação já feita — um pilar
    // com 3 de 8 questões classificadas respondia "nenhuma questão classificada".
    const temas = pilar.themes ?? [];

    return temas.map((t, posicao) => ({
      rotulo: t.title,
      nota: numero(t.score),
      // O rótulo do eixo é o tema, que não diz de qual pilar veio.
      contexto: pilar.name_pillar,
      id_pillar: Number(pilar.id_pillar),
      cor: tomDoTema(indice, posicao, temas.length),
    }));
  });
}

/**
 * Gráfico de nota por QUESTÃO — uma barra por questão, agrupadas por pilar.
 *
 * O TL pediu explicitamente que as questões não se misturem entre pilares: a
 * leitura é "todas as do pilar X, depois todas do pilar Y". O agrupamento é
 * estrutural (as questões vêm dentro do pilar) e a ordem interna é a que o
 * backend já resolveu — por tema e depois por índice da questão.
 *
 * Questão com nota zero aparece: sem resposta vale zero na nota do pilar, e é
 * assim que o cálculo sempre funcionou. Esconder zero esconderia justamente a
 * questão que ninguém avaliou.
 */
export function montarBarrasDeQuestao(pillars) {
  return (pillars ?? []).flatMap((pilar, indice) => {
    const questoes = pilar.questions ?? [];
    const temas = pilar.themes ?? [];

    const tituloDoTema = (id_theme) =>
      temas.find((t) => Number(t.id_theme) === Number(id_theme))?.title;

    return questoes.map((q) => {
      const tema = q.id_theme == null ? undefined : tituloDoTema(q.id_theme);
      const posicao = temas.findIndex(
        (t) => Number(t.id_theme) === Number(q.id_theme)
      );

      return {
        // O código é curto o bastante para caber no eixo; o texto da questão não
        // existe em tabela própria (question aponta para question_template, que
        // só tem o código).
        rotulo: q.code,
        // O texto da questão, para a legenda e o tooltip. Vem de LEFT JOIN no
        // backend: pode não existir, e undefined é o que a legenda usa para
        // omitir o item em vez de mostrar código sem descrição ao lado.
        descricao: q.description || undefined,
        nota: numero(q.score),
        contexto: tema ? `${pilar.name_pillar} › ${tema}` : pilar.name_pillar,
        id_pillar: Number(pilar.id_pillar),
        cor:
          posicao >= 0
            ? tomDoTema(indice, posicao, temas.length)
            : corDoPilar(indice),
      };
    });
  });
}
